import clsx from 'clsx'
import type { PropsWithChildren, ReactNode } from 'react'
import React from 'react'
import type { AllColors } from '../utilities/colors'

export interface AvatarProps {
  /**
   * Accessible label for the avatar.
   * If not provided, the `name` prop is be used
   */
  'aria-label'?: string
  /**
   * The content to display as initials in the avatar. If a string is given, it will extract up to 2 capital letters or first letters of words. Otherwise, the provided content will be rendered as is
   */
  children?: ReactNode
  /**
   * The color scheme of the avatar background and text
   * @default 'default'
   */
  color?: AvatarColor
  /**
   * The name to display as initials in the avatar.
   * Will extract up to 2 capital letters or first letters of words
   */
  name?: string
  /**
   * The shape variant of the avatar
   * @default 'circle'
   */
  variant?: AvatarVariant
}

export type AvatarColor = AllColors | 'green'
type AvatarColors = { [key in AllColors | 'green']: string }

type AvatarVariant = 'circle' | 'rounded' | 'square'
type AvatarVariants = {
  [key in AvatarVariant]: string
}

// TODO: Make shared values for these utility colors
export const colors: AvatarColors = {
  default: 'bg-zinc-100 text-zinc-800',
  primary: 'bg-primary-10 text-primary-darker',
  secondary: 'bg-secondary-10 text-secondary-darker',
  blue: 'bg-sky-100 text-sky-800',
  error: 'bg-red-100 text-red-800',
  green: 'bg-lime-100 text-lime-800',
  indigo: 'bg-violet-100 text-violet-700',
  pink: 'bg-pink-100 text-pink-700',
  purple: 'bg-fuchsia-100 text-fuchsia-700',
  red: 'bg-rose-100 text-rose-700',
  teal: 'bg-teal-100 text-teal-800',
  warning: 'bg-yellow-100 text-yellow-800',
  yellow: 'bg-amber-100 text-amber-800',
}

const variants: AvatarVariants = {
  circle: 'rounded-full',
  square: '',
  rounded: 'rounded-md',
}

function Avatar({
  'aria-label': label,
  children,
  color = 'default',
  name,
  variant = 'circle',
}: PropsWithChildren<AvatarProps>): JSX.Element {
  return (
    <div
      aria-label={label || name}
      className={clsx(
        'justify-center items-center text-xl inline-flex w-10 h-10 p-2',
        colors[color],
        variants[variant],
      )}
      role="img"
    >
      {name !== undefined
        ? name.match(/[A-Z]/g)?.slice(0, 2)?.join('') ||
          name
            .split(' ')
            .slice(0, 2)
            .map((word) => word.substring(0, 1).toUpperCase())
            .join('')
        : typeof children === 'string'
        ? children.substring(0, 2).toUpperCase()
        : children}
    </div>
  )
}

export default Avatar
